import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, CreditCard } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "../../components/ui/sonner";
import { ErrorState } from "../../components/common/ErrorState";
import { RotateSecretDialog } from "../../components/control/RotateSecretDialog";
import { ConnectWhatsappDialog } from "../../components/control/ConnectWhatsappDialog";
import { INTEGRATIONS } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

const STATUS_COLORS = {
  active: "var(--status-active-text)", pending: "var(--status-overdue-text)",
  failed: "var(--scarlet-neg)", inactive: "var(--scarlet-text-secondary)",
};

// UX-CP-02 §2 SCR-P08-20 "Integrations console" — cross-tenant, per-gym isolation (a gym is
// picked explicitly; nothing renders until it is). Requires `secrets`.
export default function IntegrationsConsole() {
  const [orgId, setOrgId] = useState("");
  const [rotateTarget, setRotateTarget] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();

  const gymsQ = useQuery({
    queryKey: ["control-gyms"],
    queryFn: async () => (await api.get("/control/gyms")).data.gyms,
  });

  const configsQ = useQuery({
    queryKey: ["gym-integrations", orgId],
    queryFn: async () => (await api.get(`/control/gyms/${orgId}/integrations`)).data.configs,
    enabled: !!orgId,
  });

  const testMutation = useMutation({
    mutationFn: (config) => api.post(`/control/gyms/${orgId}/integrations/${config.id}/test`).then((res) => ({ res, config })),
    onSuccess: ({ res, config }) => {
      const label = config.channel === "whatsapp" ? "WhatsApp" : config.channel.toUpperCase();
      toast[res.data.status === "ok" ? "success" : "error"](
        res.data.status === "ok" ? `Test connection: OK — ${label} is genuinely connected.` : `Test connection failed: ${res.data.reason || "unknown error"}`
      );
      queryClient.invalidateQueries({ queryKey: ["gym-integrations", orgId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const hasConfiguredWhatsapp = (configsQ.data || []).some((c) => c.channel === "whatsapp" && c.secret?.configured);

  if (gymsQ.isError) return <ErrorState error={gymsQ.error} onRetry={() => gymsQ.refetch()} title="Couldn't load Integrations" />;

  return (
    <div data-testid={INTEGRATIONS.page} className="space-y-6 max-w-4xl pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Integrations</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Connect, test, and rotate a specific gym's third-party connectors.</p>
      </motion.div>

      <div className="flex items-center justify-between max-w-lg">
        <div className="max-w-xs flex-1">
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger data-testid={INTEGRATIONS.gymSelect}><SelectValue placeholder="Choose a gym…" /></SelectTrigger>
            <SelectContent>
              {(gymsQ.data || []).map((g) => <SelectItem key={g.organization_id} value={g.organization_id}>{g.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {orgId && !hasConfiguredWhatsapp && (
          <Button data-testid={INTEGRATIONS.connectWhatsappButton} size="sm" onClick={() => setConnectOpen(true)} className="gap-1.5">
            <Plus size={14} /> Connect WhatsApp
          </Button>
        )}
      </div>

      {orgId && configsQ.isError && (
        <ErrorState error={configsQ.error} onRetry={() => configsQ.refetch()} title="Couldn't load this gym's integrations" />
      )}

      {orgId && !configsQ.isError && (
        <motion.div {...fadeUp(reducedMotion, 0.04)} className="solid-surface rounded-[14px] overflow-hidden">
          <Table data-testid={INTEGRATIONS.configsTable}>
            <TableHeader>
              <TableRow><TableHead>Channel</TableHead><TableHead>Provider</TableHead><TableHead>Status</TableHead><TableHead>Credential</TableHead><TableHead>Last tested</TableHead><TableHead>Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {configsQ.isLoading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Loading…</TableCell></TableRow>}
              {!configsQ.isLoading && (configsQ.data || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>This gym has no connectors configured.</TableCell></TableRow>
              )}
              {(configsQ.data || []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="capitalize">
                    {c.channel}
                    {c.public_config?.phone_number_id && (
                      <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>
                        {c.public_config.verified_name || c.from_address} · WABA {c.public_config.waba_id}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="font-medium" style={{ color: "var(--scarlet-text)" }}>{c.provider}</TableCell>
                  <TableCell className="capitalize" style={{ color: STATUS_COLORS[c.status] || "var(--scarlet-text-secondary)" }}>
                    {c.status}
                    {c.status_reason && <p className="text-xs mt-0.5 font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{c.status_reason}</p>}
                  </TableCell>
                  <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{c.secret.configured ? `•••• ${c.secret.secret_preview || ""}` : "Not set"}</TableCell>
                  <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{c.last_tested_at ? new Date(c.last_tested_at).toLocaleString() : "Never"}</TableCell>
                  <TableCell className="flex gap-2">
                    <Button data-testid={INTEGRATIONS.testButton(c.id)} size="sm" variant="outline" disabled={testMutation.isPending} onClick={() => testMutation.mutate(c)}>Test</Button>
                    <Button data-testid={INTEGRATIONS.rotateButton(c.id)} size="sm" variant="outline" onClick={() => setRotateTarget(c)}>Rotate</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <motion.div
        {...fadeUp(reducedMotion, 0.08)}
        data-testid={INTEGRATIONS.paymentProviderCard}
        className="solid-surface rounded-[14px] p-6 flex items-center justify-between gap-4 max-w-2xl opacity-80"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full p-2.5" style={{ backgroundColor: "var(--scarlet-surface-2)" }}>
            <CreditCard size={18} style={{ color: "var(--scarlet-text-secondary)" }} />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>Payment provider (Stripe / Razorpay)</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>
              Not connected yet — provider choice pending a platform decision.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="status-pill">Coming soon</span>
          <Button data-testid={INTEGRATIONS.paymentProviderConnectButton} size="sm" variant="outline" disabled>
            <Plus size={14} className="mr-1.5" /> Connect
          </Button>
        </div>
      </motion.div>

      <RotateSecretDialog orgId={orgId} config={rotateTarget} open={!!rotateTarget} onOpenChange={(v) => !v && setRotateTarget(null)} />
      <ConnectWhatsappDialog orgId={orgId} open={connectOpen} onOpenChange={setConnectOpen} />
    </div>
  );
}
